/**
 * The buyer contracts (Vendor buyer profile): the details a seller's checkout
 * may ask a buyer for, as a saved profile holds them and a change to it
 * gives them. Identity keeps them; Buy on behalf's seller kit, its sellers
 * and the clients' buyer forms (`@libs/buyer-identity-client`, which
 * re-exports them) read them.
 */
import { z } from "zod";
import type { CardSummary } from "./card";
export const buyerFieldIds = [
  "title",
  "firstName",
  "lastName",
  "birthYear",
  "dateOfBirth",
  "address",
  "address2",
  "city",
  "region",
  "postcode",
  "country",
  "phone",
  "gender",
  "nationality",
  "identityDocument",
] as const;
export type BuyerField = (typeof buyerFieldIds)[number];
/** The titles a checkout may ask for (AltTickets' list, read from its guest
 *  checkout on 30 Sept 2026). */
export const buyerTitles = ["Mr", "Miss", "Mrs", "Ms", "Mx", "Dr"] as const;
/** The genders a checkout may ask for, in Stager's contact form's words
 *  (22 Oct 2026: Male, Female, Non-binary). */
export const buyerGenders = ["Female", "Male", "Non-binary"] as const;
const text = z.string().trim().min(1).max(240);
const countryCode = z.string().regex(/^[A-Z]{2}$/);
/** A phone as a buyer gives it: five digits or more, an optional + and the
 *  separators people type. Saved E.164 (`readPhone`). */
const phone = z
  .string()
  .trim()
  .regex(/^(?=(?:\D*\d){5})\+?[\d ()-]{7,25}$/);
/** A year of birth, four digits (Shotgun's contact form asks it). */
const birthYear = z.string().trim().regex(/^(19|20)\d{2}$/);
/**
 * The buyer's national ID or passport number, as a seller's checkout asks it
 * (Passline's "DNI / Passport ID", and the card holder's document Mercado
 * Pago asks). Read as typed without its spaces, dots and dashes ("12.345.678"
 * is 12345678), upper case: five to twenty letters and digits. Saved with
 * the buyer's other details for the next purchase (Erik, 22 Oct 2026:
 * everything collected is held on Lissen identity for reuse); given only
 * to a checkout that declares it.
 */
export const identityDocumentSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s.-]/g, "").toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9]{5,20}$/));
/** ISO calendar date: sellers ask the buyer's actual birth date. */
const dateOfBirth = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(value + "T00:00:00Z");
    return (
      Number.isFinite(date.getTime()) &&
      date.toISOString().slice(0, 10) === value &&
      date.getTime() < Date.now()
    );
  }, "Enter a valid past date as YYYY-MM-DD");
/**
 * A buyer's saved details. Each is optional in a saved profile and required
 * by a checkout that declares it (`parseBuyerDetails`, the server's
 * `buyerSchemaFor`), as the buyer's country has it (`buyerFieldsFor`): a
 * second address line is never required, a postcode only where the
 * country's addresses have one, a region (state, county, province…) only
 * where they have one. `title` and `city` are asked for apart only by a
 * vendor whose checkout needs them (AltTickets), `birthYear` only by one
 * whose contact form asks it (Shotgun), `dateOfBirth` (an ISO date) only by
 * one whose checkout requires the whole date (Oztix). A correction to either
 * birth detail forgets the other if it no longer agrees (`nextBuyerDetails`).
 * `firstName` and `lastName`
 * are the buyer's name, asked for apart by every vendor. `name` is the whole
 * name in one, as a profile saved it before they were asked for apart: no
 * checkout declares it and no vendor is given it; a checkout is offered it
 * split as a suggestion for the first and last name (`prefillBuyerDetails`).
 *
 * `region` is the region's code where the country lists its regions (a US
 * state's "CA", `countries.json`), else as typed. `phone` is saved E.164
 * ("+447700900123") with the country it is dialled in (`phoneCountry`); a
 * profile saved before then holds it as typed, without `phoneCountry`, and
 * it is read in the address country (`readPhone`) until the buyer next
 * saves it.
 *
 * `identityDocument` (`identityDocumentSchema`) is the buyer's ID or
 * passport number, asked only by a vendor whose checkout requires it
 * (Passline), and like every detail kept for the next purchase.
 */
export const buyerSchema = z
  .object({
    title: z.enum(buyerTitles).optional(),
    firstName: text.optional(),
    lastName: text.optional(),
    birthYear: birthYear.optional(),
    dateOfBirth: dateOfBirth.optional(),
    name: text.optional(),
    address: text,
    address2: text.optional(),
    city: text.optional(),
    region: text.optional(),
    postcode: text.optional(),
    country: countryCode,
    phone,
    phoneCountry: countryCode.optional(),
    gender: z.enum(buyerGenders).optional(),
    // A nationality, as a country code: asked only by a checkout whose
    // seller requires it (Ticketmaster France's account profile, 22 Oct
    // 2026), and kept on the identity for the next purchase (Erik).
    nationality: countryCode.optional(),
    identityDocument: identityDocumentSchema.optional(),
  })
  .strict();
export type Buyer = z.infer<typeof buyerSchema>;
/** A saved profile, every detail optional. */
export const savedBuyerSchema = buyerSchema.partial();
/**
 * A change to a saved profile: each detail given replaces the saved one,
 * and null forgets it (a second line emptied, a postcode the new country's
 * addresses do not have). The country is never forgotten. See
 * `nextBuyerDetails`.
 */
export const buyerPatchSchema = z
  .object({
    title: z.enum(buyerTitles).nullish(),
    firstName: text.nullish(),
    lastName: text.nullish(),
    birthYear: birthYear.nullish(),
    dateOfBirth: dateOfBirth.nullish(),
    name: text.nullish(),
    address: text.nullish(),
    address2: text.nullish(),
    city: text.nullish(),
    region: text.nullish(),
    postcode: text.nullish(),
    country: countryCode.optional(),
    phone: phone.nullish(),
    phoneCountry: countryCode.nullish(),
    gender: z.enum(buyerGenders).nullish(),
    nationality: countryCode.nullish(),
    identityDocument: identityDocumentSchema.nullish(),
  })
  .strict();
export type BuyerPatch = z.infer<typeof buyerPatchSchema>;

/** What a buyer's checkout reads of their identity: the email, the saved
 *  details, the saved cards and the vendor account. */
export type BuyerIdentity = {
  email: string;
  details: Partial<Buyer>;
  cards: CardSummary[];
  vendorAccount: { email: string; status: "pending" | "established" } | null;
  /** Whether the buyer already has an agent-mail address on a domain no
   *  vendor refuses, which a new vendor account can share: a checkout then
   *  has no account to open ahead of Pay. False for a buyer whose only
   *  addresses some vendor refuses, whose checkout asks and is told
   *  `has_address` by a vendor that takes one. Absent from a server before
   *  it was served: nothing is opened. */
  agentMailAddress?: boolean;
};
